export const VALIDATION_CONSTANTS = {
  ADDRESS: {
    // Minimum character length for a single address field (e.g. address_line_1)
    MIN_LENGTH: 30,
    MAX_LENGTH: 200,
    // Combined alphanumeric character minimum across house_no + street + address_line_1 + address_line_2 + landmark
    // to prevent trivially short or placeholder entries like "1 St"
    MIN_ALPHANUMERIC: 30,
    // Maximum combined alphanumeric length across all address fields (house_no + street + lines + landmark + city + state + country + pincode)
    // aligns with postal system and backend storage constraints
    TOTAL_MAX_LENGTH: 175,
  },
  AADHAR: {
    // Indian government-issued 12-digit unique identity number
    LENGTH: 12,
  },
  ABHA: {
    // Ayushman Bharat Health Account number — 14-character alphanumeric identifier
    LENGTH: 14,
  },
  ZIPCODE: {
    // Indian PIN code is always exactly 6 digits
    LENGTH: 6,
  },
  AGE: {
    // Lower bound excludes paediatric patients not covered by this platform
    MIN: 10,
    // Upper bound guards against data-entry errors (e.g. birth year typos)
    MAX: 120,
  },
  DISCOUNT_CODE: {
    // Short codes are too easy to guess/brute-force
    MIN_LENGTH: 3,
    // Mirrors DiscountCode.code max_length on the API
    MAX_LENGTH: 50,
    // Letters, numbers and hyphens (batch-generated codes look like SC-A1B2-C3D4)
    PATTERN: /^[A-Z0-9-]+$/,
  },
} as const;

export const normalizeIndianPincode = (raw: string | null | undefined): string =>
  String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, VALIDATION_CONSTANTS.ZIPCODE.LENGTH);

export const VALIDATION_MESSAGES = {
  ADDRESS: {
    REQUIRED: 'Address is required.',
    MIN_ALPHANUMERIC: `Please enter a valid address with at least ${VALIDATION_CONSTANTS.ADDRESS.MIN_ALPHANUMERIC} alphanumeric characters.`,
    TOTAL_MAX_LENGTH: `The maximum allowed address length is ${VALIDATION_CONSTANTS.ADDRESS.TOTAL_MAX_LENGTH} characters. Total address = house no + street + address line 1 + address line 2 + landmark + city + state + country + pincode.`,
    STREET_MIN_LENGTH: `House no + Street + Address Line 1 + Address Line 2 + Landmark must be at least ${VALIDATION_CONSTANTS.ADDRESS.MIN_ALPHANUMERIC} alphanumeric characters combined.`,
  },
  AADHAR: {
    INVALID: `Aadhar number must be exactly ${VALIDATION_CONSTANTS.AADHAR.LENGTH} digits`,
  },
  ABHA: {
    INVALID: `ABHA number must be exactly ${VALIDATION_CONSTANTS.ABHA.LENGTH} characters`,
  },
  ZIPCODE: {
    INVALID: `PIN Code must be exactly ${VALIDATION_CONSTANTS.ZIPCODE.LENGTH} digits`,
  },
  DATE_OF_BIRTH: {
    INVALID_FORMAT: 'Invalid date format.',
    INVALID_AGE: `Date of birth not valid — age should be between ${VALIDATION_CONSTANTS.AGE.MIN} and ${VALIDATION_CONSTANTS.AGE.MAX} years`,
    FUTURE_DATE: 'Date of birth cannot be in the future',
  },
  RELATIONSHIP_STATUS: {
    REQUIRED: 'Relationship status is required.',
    OTHER_REQUIRED: 'Please specify your relationship status.',
  },
  DISCOUNT_CODE: {
    REQUIRED: 'Code is required',
    MIN_LENGTH: `Code must be at least ${VALIDATION_CONSTANTS.DISCOUNT_CODE.MIN_LENGTH} characters`,
    MAX_LENGTH: `Code must be at most ${VALIDATION_CONSTANTS.DISCOUNT_CODE.MAX_LENGTH} characters`,
    INVALID: 'Code must contain only uppercase letters, numbers, and hyphens',
  },
} as const;

export const normalizeDiscountCode = (raw: string | null | undefined): string =>
  String(raw ?? '')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toUpperCase()
    .slice(0, VALIDATION_CONSTANTS.DISCOUNT_CODE.MAX_LENGTH);

const stripSpecialChars = (value: string | null | undefined): string =>
  (value ?? '').replace(/[^a-zA-Z0-9]/g, '');

export const getTotalAddressLength = (address: {
  house_no?: string | null;
  street?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  landmark?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;
}): number => {
  const parts = [
    address.house_no,
    address.street,
    address.address_line_1,
    address.address_line_2,
    address.landmark,
    address.city,
    address.state,
    address.country,
    address.zipcode,
  ];
  return parts.map(stripSpecialChars).join('').length;
};

export const getAddressStreetLength = (address: {
  house_no?: string | null;
  street?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  landmark?: string | null;
}): number => {
  const parts = [
    address.house_no,
    address.street,
    address.address_line_1,
    address.address_line_2,
    address.landmark,
  ];
  return parts.map(stripSpecialChars).join('').length;
};
